import React, { useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';

import {
  AddQuestionButton,
  EditableSection,
  QuestionSectionWrapper,
} from './styles';
import { QuestionSection, SurveyQuestionsProps } from './types';
import { useSurveyQuestions } from './useSurveyQuestions';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { ConfirmActionModal } from '../ConfirmActionModal/ConfirmActionModal';
import QuestionTypeSelector from './elements/QuestionTypeSelector/QuestionTypeSelector';
import QuestionForm from './elements/QuestionForm/QuestionForm';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';

const SurveyQuestions: React.FC<SurveyQuestionsProps> = ({
  surveyId,
  defaultQuestion = true,
  onQuestionsUpdate,
}) => {
  const {
    questionSections,
    setQuestionSections,
    loadingQuestions,
    selectedQuestionType,
    selectedAnswerType,
    setSelectedQuestionType,
    setSelectedAnswerType,
    handleAddQuestion,
    handleSaveQuestion,
    updateQuestionSection,
    deleteSurveyQuestion,
    isQuestionValid,
    toggleQuestionEdit,
    fetchSurvey,
  } = useSurveyQuestions({
    surveyId,
    onQuestionsUpdate,
  });

  const [showTypeSelector, setShowTypeSelector] = useState(defaultQuestion);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<{
    id: string;
    text: string;
    isSaved: boolean;
  } | null>(null);

  const { t } = useI18n();

  const handleConfirmQuestionType = () => {
    handleAddQuestion();
    setShowTypeSelector(false);
  };

  const handleCancelTypeSelection = () => {
    setShowTypeSelector(false);
    setSelectedQuestionType('');
    setSelectedAnswerType('');
  };

  const handleShowTypeSelector = () => {
    setShowTypeSelector(true);
  };

  const handleDeleteQuestion = (questionId: string) => {
    const section = questionSections.find((q) => q.id === questionId);
    if (!section) return;

    setQuestionToDelete({
      id: questionId,
      text: section.text,
      isSaved: section.status.isSaved,
    });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!questionToDelete) return;

    try {
      if (questionToDelete.isSaved) {
        await deleteSurveyQuestion(surveyId, questionToDelete.id);
      }
    } catch (error) {
      if (error instanceof Error && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          console.log(
            'Question not found on server, removing from local state'
          );
        }
      } else {
        console.error('Error deleting question:', error);
      }
    } finally {
      setQuestionSections((prev: QuestionSection[]) =>
        prev.filter((q) => q.id !== questionToDelete.id)
      );
      try {
        await fetchSurvey(surveyId);
      } catch (fetchError) {
        console.error('Error refreshing survey:', fetchError);
      }
      setIsDeleteModalOpen(false);
      setQuestionToDelete(null);
    }
  };

  return (
    <>
      <EditableSection>
        <h3>{t(TranslationKeys.survey_questions_title)}</h3>
        <QuestionSectionWrapper>
          {questionSections
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((section, index) => (
              <QuestionForm
                key={section.id}
                section={section}
                index={index}
                loading={loadingQuestions[section.id]}
                onDelete={() => handleDeleteQuestion(section.id)}
                updateQuestionSection={updateQuestionSection}
                handleSaveQuestion={handleSaveQuestion}
                isQuestionValid={isQuestionValid}
                onToggleEdit={() => toggleQuestionEdit(section.id)}
              />
            ))}

          {showTypeSelector && (
            <QuestionTypeSelector
              selectedQuestionType={selectedQuestionType}
              selectedAnswerType={selectedAnswerType}
              onQuestionTypeChange={setSelectedQuestionType}
              onAnswerTypeChange={setSelectedAnswerType}
              onConfirm={handleConfirmQuestionType}
              onCancel={handleCancelTypeSelection}
            />
          )}

          {!showTypeSelector && (
            <AddQuestionButton>
              <ActionButton
                variant="accept"
                buttonText={t(TranslationKeys.survey_questions_add_question)}
                onClickAction={handleShowTypeSelector}
                icon={<AddIcon />}
                fullWidth
              />
            </AddQuestionButton>
          )}
        </QuestionSectionWrapper>
      </EditableSection>

      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Question"
        content={
          questionToDelete
            ? t(
                TranslationKeys.survey_questions_delete_modal_content_with_text,
                { text: questionToDelete.text }
              )
            : t(TranslationKeys.survey_questions_delete_modal_content_generic)
        }
      />
    </>
  );
};

export default SurveyQuestions;
